import { IconButton } from '@app/components/core/button/IconButton'
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import {
  DropdownMenu,
  type DropdownMenuItem,
} from '@app/components/core/dropdown/DropdownMenu'
import { Modal } from '@app/components/blocks/modal/Modal'
import { Text } from '@app/components/core/typography/Text'
import { useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { useToast } from '@app/util/hooks/useToast'
import { pages } from '@app/util/links'
import { DeleteInitiativeResult } from 'functions/app/dashboard/initiative/delete'
import { useCallback, useState } from 'react'
import { useLocation } from 'wouter'

export type InitiativeMenuProps = {
  initiativeId: number
}

export const InitiativeMenu = ({ initiativeId }: InitiativeMenuProps) => {
  const client = useClient()
  const [_, navigate] = useLocation()
  const translations = useTranslations().app.dashboard.initiative.view
  const { showFailToast, showSuccessToast } = useToast()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleDelete = useCallback(() => {
    setLoading(true)
    void client
      .call('delete-initiative', { id: initiativeId })
      .then((result: DeleteInitiativeResult) => {
        if (typeof result?.hasRemainingInitiatives !== 'boolean') {
          showFailToast()
          return
        }

        showSuccessToast(translations.deleteSuccess)

        if (!result.hasRemainingInitiatives) {
          navigate(pages.dashboard)
        }
      })
      .catch(() => showFailToast())
      .finally(() => {
        setLoading(false)
        setConfirmOpen(false)
      })
  }, [
    client,
    initiativeId,
    navigate,
    showFailToast,
    showSuccessToast,
    translations.deleteSuccess,
  ])

  const items: DropdownMenuItem[] = [
    {
      id: 'delete',
      label: translations.deleteButton,
      linkProps: {
        onClick: () => setConfirmOpen(true),
        textStyle: { textWrap: 'nowrap' },
      },
    },
  ]

  return (
    <>
      <DropdownMenu items={items}>
        <IconButton
          variant="secondary"
          size="small"
          icon={faEllipsisVertical}
          onClick={() => {}}
        />
      </DropdownMenu>
      <Modal
        isOpen={confirmOpen}
        loading={loading}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        confirmLabel={translations.confirm}
        backLabel={translations.cancel}
      >
        <Text.Medium style={{ lineHeight: '1.75rem', width: '20rem' }}>
          {translations.deleteConfirmDescription}
        </Text.Medium>
      </Modal>
    </>
  )
}
